import { useCallback, useEffect, useMemo, useState } from 'react';

export type RoleValue = 'viewer' | 'contributor' | 'editor' | 'manager' | 'admin' | 'owner';

type AllowlistScope = 'ORG' | 'VAULT';

interface MembershipUser {
  id: string;
  name?: string | null;
  email: string;
  image?: string | null;
}

interface OrganizationReference {
  id: string;
  name: string;
  slug: string;
}

export interface OrganizationMemberEntry {
  id: string;
  type: 'membership';
  status: 'active';
  email: string;
  role: RoleValue;
  user: MembershipUser | null;
  organization: OrganizationReference | null;
}

export interface AllowlistEntry {
  id: string;
  value: string;
  scope: AllowlistScope;
  organization: OrganizationReference | null;
  vault: { id: string; title: string; slug: string } | null;
  createdAt?: string;
}

interface UseOrganizationAccessManagementOptions {
  organizationId: string | null;
}

interface UseOrganizationAccessManagementResult {
  loading: boolean;
  error: string | null;
  members: OrganizationMemberEntry[];
  allowlist: AllowlistEntry[];
  memberCount: number;
  addMember: (params: { email: string; role: RoleValue }) => Promise<void>;
  updateMemberRole: (params: { entryId: string; role: RoleValue }) => Promise<void>;
  removeMember: (entryId: string) => Promise<void>;
  addAllowlistDomain: (domain: string) => Promise<void>;
  removeAllowlistEntry: (entryId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

async function readError(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string') {
      return data.error;
    }
    if (typeof data?.message === 'string') {
      return data.message;
    }
  } catch {
    // Ignore JSON parse errors
  }

  try {
    const text = await response.text();
    if (text) {
      return text;
    }
  } catch {
    // Ignore text parse errors
  }

  return `Request failed with status ${response.status}`;
}

export function useOrganizationAccessManagement({
  organizationId,
}: UseOrganizationAccessManagementOptions): UseOrganizationAccessManagementResult {
  const [entries, setEntries] = useState<OrganizationMemberEntry[]>([]);
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setEntries([]);
      setAllowlist([]);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/memberships', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(await readError(response));
      }
      const payload = await response.json();

      // Filter for org-level memberships only (vault === null)
      const orgMembers = (Array.isArray(payload.entries) ? payload.entries : [])
        .filter(
          (entry: any) =>
            entry.type === 'membership' &&
            entry.vault === null &&
            entry.organization?.id === organizationId,
        ) as OrganizationMemberEntry[];

      // Filter for org-scoped allowlist entries
      const orgAllowlist = (Array.isArray(payload.allowlists) ? payload.allowlists : [])
        .filter(
          (entry: any) =>
            entry.scope === 'ORG' && entry.organization?.id === organizationId,
        ) as AllowlistEntry[];

      setEntries(orgMembers);
      setAllowlist(orgAllowlist);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load access data.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!organizationId) {
        setEntries([]);
        setAllowlist([]);
        setError(null);
        return;
      }
      await refresh();
      if (!active) {
        return;
      }
    })();

    return () => {
      active = false;
    };
  }, [organizationId, refresh]);

  const members = useMemo(() => entries, [entries]);
  const memberCount = members.length;

  const addMember = useCallback(
    async ({ email, role }: { email: string; role: RoleValue }) => {
      if (!organizationId) {
        throw new Error('An organization must be selected before adding members.');
      }

      const response = await fetch('/api/memberships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role, organizationId }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setEntries((previous) => {
          const next = previous.filter((entry) => entry.id !== payload.entry.id);
          next.unshift(payload.entry as OrganizationMemberEntry);
          return next;
        });
      } else {
        await refresh();
      }
    },
    [organizationId, refresh],
  );

  const updateMemberRole = useCallback(
    async ({ entryId, role }: { entryId: string; role: RoleValue }) => {
      const response = await fetch(`/api/memberships/${entryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setEntries((previous) =>
          previous.map((entry) =>
            entry.id === payload.entry.id ? (payload.entry as OrganizationMemberEntry) : entry,
          ),
        );
      } else {
        await refresh();
      }
    },
    [refresh],
  );

  const removeMember = useCallback(
    async (entryId: string) => {
      const response = await fetch(`/api/memberships/${entryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      setEntries((previous) => previous.filter((entry) => entry.id !== entryId));
    },
    [],
  );

  const addAllowlistDomain = useCallback(
    async (domain: string) => {
      if (!organizationId) {
        throw new Error('An organization must be selected before adding an allowlist domain.');
      }

      const response = await fetch('/api/domain-allowlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          value: domain,
          scope: 'org',
          organizationId,
        }),
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      const payload = await response.json();
      if (payload?.entry) {
        setAllowlist((previous) => [...previous, payload.entry as AllowlistEntry]);
      } else {
        await refresh();
      }
    },
    [organizationId, refresh],
  );

  const removeAllowlistEntry = useCallback(
    async (entryId: string) => {
      const response = await fetch(`/api/domain-allowlist/${entryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(await readError(response));
      }

      setAllowlist((previous) => previous.filter((entry) => entry.id !== entryId));
    },
    [],
  );

  return {
    loading,
    error,
    members,
    allowlist,
    memberCount,
    addMember,
    updateMemberRole,
    removeMember,
    addAllowlistDomain,
    removeAllowlistEntry,
    refresh,
  };
}
